import { EventEmitter, Injectable, Output } from "@angular/core";
import { environment } from "../../environments/environment";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { ModalService } from "./modal.service";
import { ThirdPartyConnectionService } from "./third-party-connection-service";
import { AuthenticationResult } from "../models/authentication-result.model";
import { BiometricsAuthenticationStatus } from "../models/biometrics.authenticate-data.model";
import { WebAuthnService } from "./web-authn.service";
import { TranslateService } from "@ngx-translate/core";
import { SessionStorageService } from "./session-storage.service";
import { ErrorCodes } from "../shared/constants/error-codes.enum";
import { AuthType } from "../models/auth-type.model";
import { CookieManagerService } from "./cookie-manager.service";

@Injectable({
  providedIn: "root",
})
export class ThirdPartyLoginService {
  saveThirdPartyLoginInfoStatus = new Subject<number>();
  thirdPartyLoginData: ThirdPartyLoginObj;
  private biometricsRegistrationStatus = new BehaviorSubject<boolean>(false);
  biometricsRegDone$ = this.biometricsRegistrationStatus.asObservable();

  url: string = environment.backend.baseUrl + environment.backend.contextPath + "/rest/general-request/";
  @Output() biometricsCredentialResponse: EventEmitter<any> = new EventEmitter();

  constructor(
    private http: HttpClient,
    private modalService: ModalService,
    private thirdPartyConnectionService: ThirdPartyConnectionService,
    private webAuthnService: WebAuthnService,
    private sessionStorageService: SessionStorageService,
    private translateService: TranslateService,
    private cookieManagerService: CookieManagerService,
  ) {
  }

  get biometricsLoginUUID(): string {
    return this.cookieManagerService.getLoginUUIDForAuthType(AuthType.BIOMETRICS);
  }

  setBiometricsRegDone(status: boolean): void {
    this.biometricsRegistrationStatus.next(status);
  }

  fetchThirdPartyLoginInfo(personId: number, typeId: string): Observable<HttpResponse<FetchThirdPartyLoginBody>> {
    const fetchThirdPartyLoginData : FetchThirdPartyLoginObj = {
      filter: {
        type: "filter",
        excludeRelation: ["Person", "Type"],
        element: [
          {
            type: "field",
            name: "PersonAuthentication.typeId",
            value: [typeId],
          },
        ],
      },
      mode: "FETCH",
    };

    return this.http.post<FetchThirdPartyLoginBody>(this.url, fetchThirdPartyLoginData, {
      withCredentials: true,
      observe: "response",
    });
  }

  async saveGoogleLoginConsent(googleAuthDone?: boolean, googleAuthenticateData?: any) {
    if (!googleAuthenticateData) {
      let sessionContext = JSON.parse(sessionStorage.getItem("sessionContext"));
      googleAuthenticateData = sessionContext.googleAuthenticateData;
    }
    if (googleAuthenticateData && (googleAuthenticateData?.email !== this.thirdPartyConnectionService?.googleResponseData?.email)) {
      console.error("Session Google authenticate data and Connection Service authenticate data have different emails, connection is skipped!");
      return;
    }
    if (googleAuthDone && googleAuthenticateData && this.thirdPartyConnectionService.connectUserWithGoogle) {
      this.saveGoogleLoginData(googleAuthenticateData);
      this.thirdPartyConnectionService.connectUserWithGoogle = false;
    }
  }

  private saveGoogleLoginData(googleAuthenticateData: any) {

    let firstName = googleAuthenticateData.firstName;
    let lastName = googleAuthenticateData.lastName;
    let email = googleAuthenticateData.email;
    let googleLoginUUID = googleAuthenticateData.googleLoginUUID;
    let uad: UserAgentData = JSON.parse(googleAuthenticateData.userAgentData);

    this.thirdPartyLoginData = {
      acknowledgment: null,
      mode: "SAVE",
      items: [
        {
          type: "personAuthentication",
          id: null,
          name: firstName + " " + lastName,
          userHandle: btoa(email), // Convert email to Base64
          typeId: JSON.parse(sessionStorage.getItem("EXTERNAL_LOGIN_TYPE_GOOGLE")),
          browserCookiesUuid: googleLoginUUID,
          deviceClass: uad.DeviceClass,
          deviceName: uad.DeviceName,
          deviceBrand: uad.DeviceBrand,
          deviceFirmwareVersion: uad.DeviceFirmwareVersion,
          deviceVersion: uad.DeviceVersion,
          operatingSystemClass: uad.OperatingSystemClass,
          operatingSystemName: uad.OperatingSystemName,
          operatingSystemVersion: uad.OperatingSystemVersion,
          agentClass: uad.AgentClass,
          agentName: uad.AgentName,
          agentVersion: uad.AgentVersion,
          customDeviceName: this.thirdPartyConnectionService.customBrowserName,
        },
      ],
    };
    this.handleGoogleLoginResponse();
  }

  private handleGoogleLoginResponse() {
    this.http.post(this.url, this.thirdPartyLoginData, {
      withCredentials: true,
      observe: "response",
    }).subscribe({
      next: (response: HttpResponse<ThirdPartyLoginObj>) => {
        const responseCode = response.body.acknowledgment.responseCode;
        if (response.status === 200 && responseCode === "ACC") {
          this.saveThirdPartyLoginInfoStatus.next(response.status);
          const googleUUID = response.body.items[0].browserCookiesUuid
          if (googleUUID) {
            this.cookieManagerService.saveLoginUUIDForAuthType(googleUUID, AuthType.GOOGLE);
          }
        } else if (
          response.status === 200 &&
          (responseCode === "ERR" || responseCode === "REJ")
        ) {
          this.modalService.showErrorModal(
            response.body.acknowledgment.ackonwledgmentDetailList[0].messageCode, ErrorCodes.ERR038, false,
          );
        }
      },
      error: (error) => {
        this.modalService.showErrorModal("SYS-0001", ErrorCodes.ERR039, false);
      },
    });
  }

  public async handleBiometricsLoginResponse(promise: Promise<HttpResponse<AuthenticationResult>>): Promise<void> {

    try {
      const response = await promise;
      if (!response) {
        return;
      }

      const biometricsData = response.body?.biometricsAuthenticateData;
      if (!biometricsData) {
        throw new Error("BiometricsAuthenticateData is missing in the response.");
      }

      const credentialRequestOptions: PublicKeyCredentialCreationOptions = this.decodeCredentialOptions(biometricsData.request);

      await this.processBiometricsData(biometricsData, credentialRequestOptions);
    } catch (err) {
      console.error("Error handling biometrics login response:", err);
      alert("An error occurred while processing the biometrics login. Please try again.");
    }
  }

  private decodeCredentialOptions(request: string): any {
    return this.webAuthnService.decodeCredentialOptions(request);
  }

  private async processBiometricsData(
    biometricsData: any,
    credentialOptions: any
  ): Promise<void> {
    try {
      await this.waitForDocumentFocus();
      let credential: PublicKeyCredential | null = null;
      if (biometricsData.status === BiometricsAuthenticationStatus.AUTHENTICATION_STARTED) {
        credential = await navigator.credentials.get({publicKey: credentialOptions.publicKey}) as PublicKeyCredential;
      } else if (biometricsData.status === BiometricsAuthenticationStatus.REGISTRATION_STARTED) {

        // Verify browser supports WebAuthn
        if (!navigator.credentials) {
          throw new Error("WebAuthn is not supported by this browser.");
        }
        credential = await navigator.credentials.create({publicKey: credentialOptions.publicKey}) as PublicKeyCredential;
      }
        this.biometricsCredentialResponse.emit(credential);
    } catch (err) {
      this.handleWebAuthnError(err);
    }
  }

  private waitForDocumentFocus(): Promise<void> {
    return new Promise((resolve) => {
      if (document.hasFocus()) {
        resolve();
      } else {
        const onFocus = () => {
          window.removeEventListener("focus", onFocus);
          resolve();
        };
        window.addEventListener("focus", onFocus);
      }
    });
  }


  private handleWebAuthnError(err: Error) {
    console.error("WebAuthn error occurred:", err)

    const errorTranslations: { [key: string]: string; } = {
      NotAllowedError: "webAuthn.errors.notAllowed",
      SecurityError: "webAuthn.errors.securityError",
      TypeError: "webAuthn.errors.typeError",
    };

    if (err.name === "NotAllowedError") {
      this.sessionStorageService.setBiometricAuthenticationCancelled();
    }

    // If we have a mapped error name, translate it; otherwise, fallback to original/error message
    const translationKey = errorTranslations[err.name];
    const localizedErrorMessage = translationKey
      ? this.translateService.instant(translationKey)
      : this.translateService.instant("webAuthn.errors.genericError");

    const errorMessageToDisplay = `${localizedErrorMessage}`;
    this.modalService.showErrorModal(errorMessageToDisplay, ErrorCodes.ERR040, false);
  }
}

export interface ThirdPartyLoginObj {
  acknowledgment?: any;
  mode: string;
  items: ThirdPartyLoginItem[];
}

interface UserAgentData {
  AgentClass: string;
  AgentName: string;
  AgentVersion: string;
  DeviceBrand: string;
  DeviceClass: string;
  DeviceFirmwareVersion: string;
  DeviceName: string;
  DeviceVersion: string;
  OperatingSystemClass: string;
  OperatingSystemName: string;
  OperatingSystemVersion: string;
}

export interface ThirdPartyLoginItem {
  id: number;
  acceptNotifications?: boolean;
  insertTime?: string;
  personId?: number;
  name?: string;
  userHandle?: string;
  text?: string;
  type: any;
  typeId?: number;
  uuid?: string;
  browserCookiesUuid?: string,
  deviceClass?: string,
  deviceName?: string,
  deviceBrand?: string,
  deviceFirmwareVersion?: string,
  deviceVersion?: string,
  operatingSystemClass?: string,
  operatingSystemName?: string,
  operatingSystemVersion?: string,
  agentClass?: string,
  agentName?: string,
  agentVersion?: string,
  customDeviceName?: string,
  isCurrentlyValid?: boolean
}

export interface FetchThirdPartyLoginBody {
  acknowledgment?: any;
  filter: string[];
  items: ThirdPartyLoginItem[];
  mode: string;
  pagination: number[];
}

export interface FetchThirdPartyLoginObj {
  acknowledgment?: any;
  filter: {
    type: string;
    excludeRelation?: string[];
    element: [
      {
        type: string;
        name: string;
        value?: any[]
      }
    ];
  };
  mode: string;
}
